import { User } from '~/monolith-api';
import { Stage, Assessment } from './Stage';

type StageEditorModalName = 'etm-search-modal' | 'guide-search-modal' | 'wiki-search-modal';

export type StageEditorState = {
   selectedStageIndex: null | number;
   openModal: null | StageEditorModalName;
};

type InterfaceOptions = {
   stages: Stage[];
   onStagesChange: (stages: Stage[]) => any;

   state: StageEditorState;
   onStateChange: (state: StageEditorState) => any;

   siteSettings: Record<string, string>;

   user: User | null | undefined;
};

export type AddOption = {
   name: 'guide' | 'wiki' | 'external-training-module' | 'trainer-sign-off';
   isDisabled: boolean;
};

export class StageEditorInterface {
   public stages: Stage[];
   private onStagesChange: (stages: Stage[]) => any;

   private state: StageEditorState;
   private onStateChange: (stages: StageEditorState) => any;

   private siteSettings: Record<string, string>;
   private user: User | null | undefined;

   constructor(options: InterfaceOptions) {
      this.stages = options.stages;
      this.onStagesChange = options.onStagesChange;

      this.state = options.state;
      this.onStateChange = options.onStateChange;

      this.siteSettings = options.siteSettings;

      this.user = options.user;
   }

   public get isETMFeatureActive() {
      return !!this.siteSettings['feature-external-training-modules'];
   }

   /**
    * Get Options for adding a stage or assessment
    *    at a given index
    */
   getAddMenuOptions(index: number) {
      const options: AddOption[] = [];

      // Guide and Wiki Stages can always be added
      options.push({
         name: 'guide',
         isDisabled: false,
      });
      options.push({
         name: 'wiki',
         isDisabled: false,
      });

      // ETM Stages can always be added if the feature is on
      if (this.isETMFeatureActive) {
         options.push({
            name: 'external-training-module',
            isDisabled: !this.user?.permissions.canIManageExternalTrainingModules,
         });
      }

      // Stage Sign-Offs and Quizzes are disabled at some indexes
      const priorStage = this.stages[index - 1];
      const priorStageHasAssessment = !!priorStage?.assessments?.length;
      const assessmentsDisabled = index === 0 || priorStageHasAssessment;
      options.push({
         name: 'trainer-sign-off',
         isDisabled: assessmentsDisabled,
      });

      return options;
   }

   handleAddMenuSelection(index: number, name: AddOption['name']) {
      switch (name) {
         case 'guide':
            this.onStateChange({
               ...this.state,

               // Prompt the user to select a guide to add
               openModal: 'guide-search-modal',

               // Save the index where we want to add the stage for later
               selectedStageIndex: index,
            });
            break;
         case 'wiki':
            this.onStateChange({
               ...this.state,

               // Prompt the user to select a guide to add
               openModal: 'wiki-search-modal',

               // Save the index where we want to add the stage for later
               selectedStageIndex: index,
            });
            break;
         case 'external-training-module':
            this.onStateChange({
               ...this.state,
               // Prompt the user to select a ETM module to add
               openModal: 'etm-search-modal',

               // Save the index where we want to add the stage for later
               selectedStageIndex: index,
            });
            break;
         case 'trainer-sign-off':
            // There is no intermediate step for adding a sign-off
            //    assessment, go ahead and add it direclty, and specify the
            //    index to add it at
            this.addAssessment(
               {
                  assessmentid: null,
                  assessmenttype: 'trainer-sign-off',
                  skills_points_possible: 0,
               },
               index
            );
            break;
      }
   }

   addStage(stage: Stage) {
      const { selectedStageIndex } = this.state;

      if (selectedStageIndex === null) {
         throw Error('Tried to add a stage without selecting an index');
      }

      const alreadyContainsDoc = this.stages.some(
         existingStage =>
            existingStage.docid === stage.docid && existingStage.doctype === stage.doctype
      );

      if (!alreadyContainsDoc) {
         // Add the stage
         this.onStagesChange([
            // Copy all of the other stages that will be before the new stage
            ...this.stages.slice(0, selectedStageIndex),

            stage,

            // Copy all of the other stages that will be after the new stage
            ...this.stages.slice(selectedStageIndex),
         ]);
      }

      // Clear the related temporary state
      this.onStateChange({
         ...this.state,
         selectedStageIndex: null,
         openModal: null,
      });
   }

   updateStage(targetStage: Stage, newValues: Stage) {
      this.onStagesChange(
         this.stages.map(stage => {
            if (stage === targetStage) {
               return newValues;
            } else {
               return stage;
            }
         })
      );
   }

   removeStage(targetStage: Stage) {
      this.onStagesChange(
         this.stages.filter(stage => {
            return stage !== targetStage;
         })
      );
   }

   canMoveStageEarlier(stage: Stage): boolean {
      const firstStage = this.stages[0];
      return stage !== firstStage;
   }

   moveStageEarlier(targetStage: Stage) {
      const currentIndex = this.stages.indexOf(targetStage);
      const newIndex = currentIndex - 1;
      this.moveStage(targetStage, newIndex);
   }

   canMoveStageLater(stage: Stage): boolean {
      const lastStage = this.stages[this.stages.length - 1];
      return stage !== lastStage;
   }

   moveStageLater(targetStage: Stage) {
      const currentIndex = this.stages.indexOf(targetStage);
      const newIndex = currentIndex + 1;
      this.moveStage(targetStage, newIndex);
   }

   private moveStage(targetStage: Stage, newIndex: number) {
      const otherStages = this.stages.filter(otherStage => otherStage !== targetStage);

      this.onStagesChange([
         // Copy all of the other stages that will be before the moved stage
         ...otherStages.slice(0, newIndex),

         // Copy the moved stage
         targetStage,

         // Copy the of the other stages that will be after the moved stage
         ...otherStages.slice(newIndex),
      ]);
   }

   /* Accept an index as a parameter, since we don't
       set 'selectedStageIndex' in state when adding assessments */
   addAssessment(assessment: Assessment, index = this.state.selectedStageIndex) {
      if (index === null) {
         throw Error('Tried to add an assessment without selecting a valid index');
      }

      const priorStage = this.stages[index - 1];
      const priorStageHasAssessment = !!priorStage?.assessments?.length;

      if (!priorStage || priorStageHasAssessment) {
         throw Error('Tried to add an assessment without selecting a valid index');
      }

      this.updateStage(priorStage, {
         ...priorStage,
         assessments: [assessment],
      });

      // Clear the related temporary state
      this.onStateChange({
         ...this.state,
         selectedStageIndex: null,
         openModal: null,
      });
   }

   updateAssessment(targetAssesment: Assessment, newValues: Assessment) {
      const containingStage = this.stages.find(stage =>
         stage.assessments?.includes(targetAssesment)
      );

      if (!containingStage) {
         throw Error('Cannot find target assessment in stage list');
      }

      const priorAssessments = containingStage.assessments ?? [];

      this.updateStage(containingStage, {
         ...containingStage,
         assessments: priorAssessments.map(assessment => {
            if (assessment === targetAssesment) {
               return newValues;
            } else {
               return assessment;
            }
         }),
      });
   }

   removeAssessment(targetAssesment: Assessment) {
      const containingStage = this.stages.find(stage =>
         stage.assessments?.includes(targetAssesment)
      );

      if (!containingStage) {
         throw Error('Cannot find target assessment in stage list');
      }

      const priorAssessments = containingStage.assessments ?? [];

      this.updateStage(containingStage, {
         ...containingStage,
         assessments: priorAssessments.filter(assessment => {
            return assessment !== targetAssesment;
         }),
      });
   }

   get isGuideSearchModalOpen() {
      return this.state.openModal === 'guide-search-modal';
   }

   get isWikiSearchModalOpen() {
      return this.state.openModal === 'wiki-search-modal';
   }

   get isETMSearchModalOpen() {
      return this.state.openModal === 'etm-search-modal';
   }

   closeOpenModal() {
      this.onStateChange({
         ...this.state,
         openModal: null,
      });
   }

   stageUniqueIds = new Map<any, number>();

   stageUniqueIdSerial = 1;

   getUniqueIdForStage(stage: Stage) {
      if (!this.stageUniqueIds.has(stage)) {
         this.stageUniqueIds.set(stage, this.stageUniqueIdSerial++);
      }
      return this.stageUniqueIds.get(stage);
   }
}
